<script setup lang="ts">
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import { computed, toRefs } from 'vue'
import { LinkType } from 'frontcore/views/link/types'
import { RouteLocationRaw } from 'vue-router'

interface Props {
  linkText: string
  to: RouteLocationRaw
  dataTest?: string
  linkType?: LinkType
  isExternal?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  dataTest: 'core-link',
  linkType: LinkType.PRIMARY,
  isExternal: false,
})

const {
  linkText,
  to,
  dataTest,
  isExternal,
} = toRefs(props)

const component = computed(() => {
  return isExternal?.value ? 'a' : 'router-link'
})

const extraComponentProps = computed(() => {
  const _extraComponentProps: {
    href?: string,
  } = {}

  if (isExternal.value) {
    _extraComponentProps.href = to.value.toString()
  }

  return _extraComponentProps
})

const target = computed(() => {
  return isExternal?.value ? '_blank' : ''
})

</script>

<template>
  <component
    :is="component"
    v-bind="extraComponentProps"
    class="tw-inline body--body-01"
    :class="{
      'link-primary': linkType === LinkType.PRIMARY,
      'link-ghost': linkType === LinkType.GHOST
    }"
    :data-test="dataTest"
    :target="target"
    :to="to">
    {{ linkText }}
    <SVGIcon
      v-if="isExternal"
      colorClass="tw-inline tw-mb-1"
      iconName="OpenInNew"
      :data-test="dataTest + '-external-icon'"
      :remSize="1.0" />
  </component>
</template>

<style scoped>
.link-primary {
  color: var(--core-primary-50);
  fill: var(--core-primary-50);
  text-decoration-color: var(--core-primary-50);
}

.link-ghost {
  color: var(--core-primary-10);
  fill: var(--core-primary-10);
  text-decoration-color: var(--core-primary-10);
}

.link-primary:hover, .link-ghost:hover {
  text-decoration-line: underline;
}

.link-primary:focus, .link-ghost:focus {
  box-shadow: var(--s-0) var(--s-0) var(--s-1) var(--s-0) #75ADFF;
}

.link-primary:visited, .link-ghost:visited {
  color: var(--extra-purple-30);
  fill: var(--extra-purple-30);
  text-decoration-color: var(--extra-purple-30);
}

</style>
